/**
 * «Xavf markazi» — the risk centre.
 *
 * ============================================================================
 * ONE SCREEN FOR EVERY OPEN QUESTION, RANKED, WITH THE ACTION ON THE ROW.
 * ============================================================================
 *
 * Four things in this system can be wrong at once and each had its own screen:
 * a kiosk in trouble (alerts), a working day that does not add up (attendance
 * anomalies), a machine the auto-remediation gave up on (the halted list), and
 * a fleet the panel cannot see (offline). An operator arriving in the morning
 * had to visit four screens and hold the comparison in their head.
 *
 * This ranks them together. That is the whole idea: risk is comparative, and
 * «which of these matters most right now» is a question no single-source screen
 * can answer.
 *
 * ============================================================================
 * EVERY SCORE IS SHOWN WITH ITS REASON. NOT NEGOTIABLE.
 * ============================================================================
 *
 * A number beside a person's name is an accusation they cannot answer. The
 * `RiskRow` component refuses to render without a reason string for exactly
 * that purpose, and the scores here are arithmetic an operator could redo by
 * hand from the visible facts — no model, no weighting nobody can inspect.
 *
 * The staff rows are the ones to be careful about, and they are treated
 * differently on purpose: an attendance anomaly is a QUESTION about a record,
 * never a finding about a person. §7.8's rules detect things like a check-in
 * with no check-out, which is most often a kiosk that lost power. So the action
 * offered is «review», the outcomes include «explained», and the row never
 * carries a word about the individual — only about the data.
 */
import { useCallback, useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { hasKey, t } from '../i18n/index.js';
import { ageLabel } from '../lib/format.js';
import { statusOf, useFleet } from '../realtime/useFleet.js';
import { Button, Notice, Panel, PanelHeader, Skeleton } from '../ui/index.jsx';
import { CountCards, PageHeader, RiskRow, StreamRow } from '../ui/console.jsx';
import { LiveBadge, StatChip, type Tone } from '../ui/data.jsx';

type MbtAlert = components['schemas']['MbtAlert'];
type Anomaly = components['schemas']['MbtAttendanceAnomaly'];
type Remediation = components['schemas']['MbtRemediation'];

type Lens = 'all' | 'kiosk' | 'staff' | 'system';

interface Signal {
  readonly id: string;
  readonly lens: Lens;
  readonly kind: string;
  readonly title: string;
  readonly context?: string;
  readonly reason: string;
  readonly score: number;
  readonly tone: Tone;
  readonly at: Date;
  readonly alert?: MbtAlert;
  readonly anomaly?: Anomaly;
}

/**
 * The score, as arithmetic anybody can redo from what the row shows.
 *
 * A critical alert outranks a warning; an alert nobody has acknowledged
 * outranks one somebody is already holding; and age adds slowly. Escalation is
 * the heaviest single term because a signal that has climbed §4.8's ladder has
 * already been ignored by a named person at each rung.
 *
 * Capped at 99. Not for neatness — a score that can reach 100 invites being
 * read as a percentage, and this is a rank, not a probability.
 */
function scoreAlert(alert: MbtAlert, now: Date): number {
  const hours = (now.getTime() - new Date(alert.opened_at).getTime()) / 3_600_000;
  const base = alert.severity === 'critical' ? 55 : 30;
  const unacknowledged = alert.acknowledged_at ? 0 : 12;
  const escalation = alert.escalated_level * 8;
  const age = Math.min(20, hours);
  return Math.min(99, Math.round(base + unacknowledged + escalation + age));
}

export function RiskScreen(): React.JSX.Element {
  const { expire, allows } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  const fleet = useFleet(onExpired);
  const mayCorrect = allows('mbt.attendance.correct');
  const mayCommand = allows('mbt.command.issue');

  const [alerts, setAlerts] = useState<MbtAlert[] | null>(null);
  const [anomalies, setAnomalies] = useState<Anomaly[] | null>(null);
  const [halted, setHalted] = useState<Remediation['id'] extends never ? never[] : {
    device_id: string;
    short_code: string;
    mahalla_name: string;
  }[]>([]);
  const [lens, setLens] = useState<Lens>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const settle = async <T,>(run: () => Promise<T>, set: (value: T) => void): Promise<void> => {
      try {
        set(await run());
      } catch (caught) {
        if (caught instanceof ApiError && caught.status === 401) onExpired();
        // A 403 is this operator's scope, not a failure worth a banner.
      }
    };
    await Promise.all([
      settle(
        async () =>
          (unwrap(await api().GET('/mbt/fleet/alerts', {})).items ?? []) as MbtAlert[],
        setAlerts,
      ),
      settle(
        async () =>
          (unwrap(await api().GET('/mbt/attendance/anomalies', {})).items ?? []) as Anomaly[],
        setAnomalies,
      ),
      settle(
        async () => unwrap(await api().GET('/mbt/remediation', {})).halted ?? [],
        setHalted,
      ),
    ]);
  }, [onExpired]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 30_000);
    return () => clearInterval(timer);
  }, [load]);

  async function acknowledge(alert: MbtAlert): Promise<void> {
    setBusy(alert.id);
    try {
      await api().PATCH('/mbt/fleet/alerts/{alert_id}', {
        params: { path: { alert_id: alert.id } },
        body: { acknowledge: true },
      });
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('risk.actionFailed'));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Close an attendance question.
   *
   * THE PANEL COULD NOT DO THIS BEFORE. `reviewMbtAttendanceAnomaly` has always
   * existed and no screen called it, so the anomaly queue could only ever grow
   * — a list of open questions about people's working days that nobody had a
   * button to answer. `explained` is offered first and deliberately: most of
   * these are a kiosk that lost power, not a person who did anything.
   */
  async function review(anomaly: Anomaly, resolution: 'explained' | 'confirmed'): Promise<void> {
    setBusy(anomaly.id);
    try {
      await api().PATCH('/mbt/attendance/anomalies/{anomaly_id}', {
        params: { path: { anomaly_id: anomaly.id } },
        body: {
          resolution,
          note:
            resolution === 'explained' ? t('risk.noteExplained') : t('risk.noteConfirmed'),
        },
      });
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) onExpired();
      else setError(t('risk.actionFailed'));
    } finally {
      setBusy(null);
    }
  }

  const now = fleet.now;
  const signals: Signal[] = [];

  for (const alert of alerts ?? []) {
    signals.push({
      id: alert.id,
      lens: 'kiosk',
      kind: t('risk.kindKiosk'),
      title: `${alert.short_code} — ${alert.mahalla_name}`,
      context: alert.assigned_to_name
        ? t('risk.assignedTo', { name: alert.assigned_to_name })
        : undefined,
      reason: hasKey(`alert.rule.${alert.rule}`) ? t(`alert.rule.${alert.rule}`) : alert.rule,
      score: scoreAlert(alert, now),
      tone: alert.severity === 'critical' ? 'crit' : 'warn',
      at: new Date(alert.opened_at),
      alert,
    });
  }

  for (const anomaly of anomalies ?? []) {
    signals.push({
      id: anomaly.id,
      lens: 'staff',
      kind: t('risk.kindStaff'),
      title: anomaly.full_name,
      context: anomaly.mahalla_name,
      reason: hasKey(`anomaly.${anomaly.rule}`) ? t(`anomaly.${anomaly.rule}`) : anomaly.rule,
      /* A fixed, modest score. An attendance question is not ranked against a
         dead kiosk by severity — it is a record to check, and inflating it to
         compete would put a person above a broken machine on this list. */
      score: 35,
      tone: 'info',
      at: new Date(anomaly.detected_at),
      anomaly,
    });
  }

  for (const device of halted) {
    signals.push({
      id: `halted-${device.device_id}`,
      lens: 'system',
      kind: t('risk.kindSystem'),
      title: `${device.short_code} — ${device.mahalla_name}`,
      reason: t('risk.remediationHalted'),
      /* The highest fixed score on the screen. The circuit breaker tripping
         means the system tried, failed repeatedly, and STOPPED — so nothing is
         working on this machine unless a person does it. */
      score: 90,
      tone: 'crit',
      at: now,
    });
  }

  for (const kiosk of fleet.kiosks) {
    const status = statusOf(kiosk, now);
    if (status !== 'offline') continue;
    signals.push({
      id: `offline-${kiosk.id}`,
      lens: 'kiosk',
      kind: t('risk.kindKiosk'),
      title: `${kiosk.short_code} — ${kiosk.mahalla_name}`,
      context: kiosk.telemetry_at
        ? t('risk.lastSeen', { at: ageLabel(new Date(kiosk.telemetry_at), now, t) })
        : t('fleet.notEnrolled'),
      reason: t('risk.offline'),
      score: 70,
      tone: 'crit',
      at: kiosk.telemetry_at ? new Date(kiosk.telemetry_at) : now,
    });
  }

  const ranked = signals
    .filter((signal) => lens === 'all' || signal.lens === lens)
    .sort((a, b) => b.score - a.score || b.at.getTime() - a.at.getTime());

  const loading = alerts === null && anomalies === null;
  const criticalCount = signals.filter((s) => s.tone === 'crit').length;

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={
          <>
            <LiveBadge label={t('risk.eyebrow')} />
            <span>{t('risk.scope')}</span>
          </>
        }
        title={t('nav.risk')}
        subtitle={t('risk.subtitle')}
        right={
          <p className="text-hero font-bold tabular-nums leading-none text-ink-100">
            {signals.length}
          </p>
        }
      />

      {error ? <Notice tone="warn">{error}</Notice> : null}

      <CountCards
        value={lens}
        onChange={setLens}
        cards={[
          { value: 'all', label: t('risk.lensAll'), count: signals.length, tone: criticalCount > 0 ? 'crit' : 'ok' },
          {
            value: 'kiosk',
            label: t('risk.lensKiosk'),
            count: signals.filter((s) => s.lens === 'kiosk').length,
            tone: 'warn',
          },
          {
            value: 'staff',
            label: t('risk.lensStaff'),
            count: signals.filter((s) => s.lens === 'staff').length,
            tone: 'info',
          },
          {
            value: 'system',
            label: t('risk.lensSystem'),
            count: signals.filter((s) => s.lens === 'system').length,
            tone: 'crit',
          },
        ]}
      />

      <div className="grid gap-4 xl:grid-cols-[2fr_1fr]">
        <Panel>
          <PanelHeader title={t('risk.ranking')} aside={t('risk.rankingHint')} />
          {loading ? (
            <div className="px-4 py-4">
              <Skeleton className="h-64" />
            </div>
          ) : ranked.length === 0 ? (
            /* An explicit clean state. An empty panel on a risk screen reads as
               "not loaded", which is the one misreading that matters here. */
            <p className="px-4 py-8 text-center text-label text-ok">{t('risk.clear')}</p>
          ) : (
            <div>
              {ranked.map((signal, index) => (
                <RiskRow
                  key={signal.id}
                  rank={index + 1}
                  kind={signal.kind}
                  title={signal.title}
                  context={signal.context}
                  reason={signal.reason}
                  score={signal.score}
                  tone={signal.tone}
                  actions={
                    signal.alert && mayCommand && !signal.alert.acknowledged_at ? (
                      <Button
                        tone="quiet"
                        onClick={() => void acknowledge(signal.alert as MbtAlert)}
                        disabled={busy === signal.id}
                      >
                        {t('risk.acknowledge')}
                      </Button>
                    ) : signal.anomaly && mayCorrect ? (
                      <>
                        <Button
                          tone="quiet"
                          onClick={() => void review(signal.anomaly as Anomaly, 'explained')}
                          disabled={busy === signal.id}
                        >
                          {t('risk.explained')}
                        </Button>
                        <Button
                          tone="quiet"
                          onClick={() => void review(signal.anomaly as Anomaly, 'confirmed')}
                          disabled={busy === signal.id}
                        >
                          {t('risk.confirmed')}
                        </Button>
                      </>
                    ) : signal.alert?.acknowledged_at ? (
                      <StatChip tone="info" label={t('risk.held')} />
                    ) : null
                  }
                />
              ))}
            </div>
          )}
        </Panel>

        <Panel>
          <PanelHeader title={t('risk.stream')} aside={t('risk.streamHint')} />
          {loading ? (
            <div className="px-4 py-4">
              <Skeleton className="h-64" />
            </div>
          ) : (
            <ul>
              {[...signals]
                .sort((a, b) => b.at.getTime() - a.at.getTime())
                .slice(0, 12)
                .map((signal) => (
                  <StreamRow
                    key={signal.id}
                    tone={signal.tone}
                    title={signal.reason}
                    detail={signal.title}
                    age={ageLabel(signal.at, now, t)}
                  />
                ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
